import { utcToZonedTime } from 'date-fns-tz';
import { memo } from 'react';

import { FlightTurnaroundDto, ResourceType } from '../../../../../../backend';
import { useIntlValue } from '../../../../../../contexts/IntlContext';
import { useCurrentUserClientTimezone } from '../../../../../../contexts/OidcContext/OidcContext.selectors';
import { useDateFnsFormat } from '../../../../../../hooks/useDateFnsFormat';
import { TimeDisplay } from '../../../../../navigation/ToggleUTCLocal/TimeDisplays/TimeDisplay';
import { localeToShortDateFormat } from '../../../../../time/time.utils';
import { useRmsGanttValue } from '../../../RmsContext';
import { StyledDisplayDataBox } from './DisplayTurnaroundData.style';

/**
 * True for multi day occupation where:
 * - turnaround has arrival and departure flight (not dry arr/dep)
 * or
 * - prev case false (dry arr/dep) and no flight is considered (arr/dep date not today)
 * **/
const shouldShowMultiDayTurnaroundFlightDates = (turnaround: FlightTurnaroundDto) =>
    turnaround.multiDaysOccupation &&
    ((!!turnaround.arrivalFlight && !!turnaround.departureFlight) ||
        (!turnaround.considerArrivalFlight && !turnaround.considerDepartureFlight));

export const DisplayTurnaroundData = memo(function DisplayTurnaroundData({
    turnaround,
}: {
    turnaround: FlightTurnaroundDto;
}) {
    const timezone = useCurrentUserClientTimezone();
    const resourceType = useRmsGanttValue('resourceType');
    const format = useDateFnsFormat();
    const locale = useIntlValue('locale');

    const occupationStart = turnaround.multiDaysOccupation
        ? turnaround.arrivalFlight.actualResourceOccupations?.[resourceType]?.occupationStart ??
          turnaround.departureFlight.actualResourceOccupations?.[resourceType]?.occupationStart
        : turnaround.actualOccupation.occupationStart;

    const occupationEnd = turnaround.multiDaysOccupation
        ? turnaround.arrivalFlight.actualResourceOccupations?.[resourceType]?.occupationEnd ??
          turnaround.departureFlight.actualResourceOccupations?.[resourceType]?.occupationEnd
        : turnaround.actualOccupation.occupationEnd;

    const showMultiDayTurnaroundInfo = shouldShowMultiDayTurnaroundFlightDates(turnaround);
    // Date of the displayed time (which may differ from the flight scheduled day)
    const formatDisplayedDate = (time: number) =>
        format(utcToZonedTime(time, timezone || ''), localeToShortDateFormat[locale]);

    return (
        <StyledDisplayDataBox>
            {occupationStart?.type ? (
                <>
                    {resourceType !== ResourceType.BOARDING_GATE && (
                        <>
                            <div>{occupationStart.type}</div>
                            &thinsp;
                        </>
                    )}
                    {showMultiDayTurnaroundInfo && !turnaround.considerArrivalFlight && !!turnaround.arrivalFlight && (
                        <>
                            <div>{formatDisplayedDate(occupationStart.time)}</div>
                            &thinsp;
                        </>
                    )}
                    <TimeDisplay
                        time={occupationStart?.time}
                        blink={false}
                        timezone={timezone}
                        undisplayedZone={true}
                    />
                </>
            ) : (
                <div>-</div>
            )}
            &emsp;
            <div>|</div>
            &emsp;
            {occupationEnd?.type ? (
                <>
                    {resourceType !== ResourceType.BOARDING_GATE && (
                        <>
                            <div>{occupationEnd.type}</div>
                            &thinsp;
                        </>
                    )}
                    {showMultiDayTurnaroundInfo &&
                        !turnaround.considerDepartureFlight &&
                        (turnaround.departureFlight?.flightNumber || turnaround.departureFlight?.callsign) && (
                            <>
                                <div>{formatDisplayedDate(occupationEnd.time)}</div>
                                &thinsp;
                            </>
                        )}
                    <TimeDisplay time={occupationEnd?.time} blink={false} timezone={timezone} undisplayedZone={true} />
                </>
            ) : (
                <div>-</div>
            )}
        </StyledDisplayDataBox>
    );
});
